import { analyticsAPI } from "@/lib/api";
import { getSessionId } from "./session";
import { mergeAttribution } from "./attribution";
import { safeGetItem } from "./safeStorage";

// ---- Eventos Meta DESATIVADOS (auditoria de tracking) ----
//
// A plataforma agora só envia à Meta 4 eventos: PageView, ViewContent,
// public_cta_click e checkout_viewed — todos em src/lib/metaPixel.ts.
// Lead, InitiateCheckout, Purchase, CompleteRegistration e a correspondência
// avançada automática (fbqSetAdvancedMatching) pararam de disparar por regra
// explícita do pedido ("os eventos antigos continuam no histórico do
// Gerenciador, mas ficam sem atividade futura") — eram eventos de negócio
// (cadastro, pagamento) que não deveriam ir à Meta, e alguns disparavam em
// páginas autenticadas (/paciente) que não carregam mais o Pixel.
//
// As funções abaixo viraram no-ops deliberados — mantidas só para não quebrar
// imports existentes em 8 arquivos (PatientRegisterWizard, useAuth,
// PatientDashboard, payment/page, success/page, agendar/page, consultar/page).
// Não remover os call-sites: são seguros, só não fazem mais nada.

export function fbqSetAdvancedMatching(_data: {
  em?: string; ph?: string; fn?: string; ln?: string; external_id?: string;
}) { /* desativado — ver comentário acima */ }

export function fbqLead(_params?: { content_name?: string; content_category?: string; value?: number; currency?: string }) { /* desativado */ }

export function fbqInitiateCheckout(_params?: { content_name?: string; value?: number; currency?: string }) { /* desativado */ }

export function fbqPurchase(_params: { value: number; currency: string; content_name: string; transaction_id?: string }) { /* desativado */ }

export function fbqCompleteRegistration(_params?: { content_name?: string }) { /* desativado */ }

// ---- Generic trackEvent (analytics INTERNO — nunca vai para a Meta) ----
//
// IMPORTANTE: esta função NÃO envia nada ao Meta Pixel/CAPI. Só os 4 eventos
// definidos em src/lib/metaPixel.ts (PageView, ViewContent, public_cta_click,
// checkout_viewed) chegam à Meta. Cadastro, pagamento, consulta, agendamento,
// emergência, chat, fila, médico, prontuário e prescrição continuam sendo
// medidos aqui (Postgres interno + GA4), mas nunca são repassados à Meta.

// Aparelho e navegador (24/10/2026, Marketing → Pacientes e segmentos): celular/computador,
// sistema e se abriu pelo navegador interno de um app (Instagram, Facebook, WhatsApp, TikTok).
let deviceInfoCache: { device: string; os: string | null; in_app: string | null } | null = null;
function deviceInfo() {
  if (deviceInfoCache || typeof navigator === "undefined") return deviceInfoCache;
  const u = navigator.userAgent || "";
  const in_app = /Instagram/i.test(u) ? "Instagram" : /FBAN|FBAV|FB_IAB|FBIOS/i.test(u) ? "Facebook"
    : /WhatsApp/i.test(u) ? "WhatsApp" : /TikTok|musical_ly|BytedanceWebview/i.test(u) ? "TikTok" : /GSA\//.test(u) ? "App do Google" : null;
  const os = /iPhone|iPad|iPod/i.test(u) ? "iOS" : /Android/i.test(u) ? "Android" : /Windows/i.test(u) ? "Windows" : /Macintosh|Mac OS X/i.test(u) ? "Mac" : null;
  const device = /iPad|Tablet/i.test(u) ? "Tablet" : /Mobi|iPhone|Android/i.test(u) ? "Celular" : "Computador";
  deviceInfoCache = { device, os, in_app };
  return deviceInfoCache;
}

/**
 * Evento enviado quando a pessoa SAI da página (pagehide / aba escondida): usa navigator.sendBeacon, que o navegador
 * entrega mesmo fechando a aba — um POST comum seria cortado no meio. Mesma origem e enriquecimento do trackEvent.
 * (30/10/2026 — saída da landing do Emagrecimento com o tempo e a última seção vista.)
 */
export function trackEventOnExit(eventName: string, category?: string, params?: Record<string, any>) {
  try {
    const enriched = mergeAttribution(params || {});
    const dv = deviceInfo();
    if (dv && enriched.device == null) { enriched.device = dv.device; enriched.os = dv.os; if (dv.in_app) enriched.in_app = dv.in_app; }
    const body = JSON.stringify({ event_type: eventName, page: category || "", metadata: enriched, session_id: getSessionId() });
    const blob = new Blob([body], { type: "application/json" });
    if (typeof navigator !== "undefined" && navigator.sendBeacon && navigator.sendBeacon("/api/analytics/event", blob)) return;
    analyticsAPI.trackEvent({ event_type: eventName, page: category || "", metadata: enriched, session_id: getSessionId() }).catch(() => {});
  } catch { /* medição nunca derruba a página */ }
}

export function trackEvent(
  eventName: string,
  category?: string,
  params?: Record<string, any>
) {
  // Preserva a atribuição de origem (UTMs, fbclid, gclid, fbp, fbc) capturada no
  // primeiro toque da sessão em TODOS os eventos — o admin de Marketing usa isso
  // para relacionar visita → cadastro → checkout → pedido → pagamento.
  const enriched = mergeAttribution(params || {});
  // Paciente logado: todo evento leva o número dele (Marketing → Jornada mostra onde cada um parou).
  const dv = deviceInfo();
  if (dv && enriched.device == null) { enriched.device = dv.device; enriched.os = dv.os; if (dv.in_app) enriched.in_app = dv.in_app; }
  if (enriched.case_id == null) {
    const cid = Number(String(safeGetItem("logged_patient_case_id") || "").replace(/\D/g, ""));
    if (Number.isFinite(cid) && cid > 0) enriched.case_id = cid;
  }
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("event", eventName, { event_category: category, ...enriched });
  }

  // Medição interna independente da Meta — sempre grava, mesmo se o Pixel
  // nunca tiver sido carregado nesta página (áreas autenticadas, por ex.).
  // Fire-and-forget — network failures here must not surface as errors.
  analyticsAPI.trackEvent({
    event_type: eventName,
    page: category || "",
    metadata: enriched,
    session_id: getSessionId(),
  }).catch(() => {});
}